import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { MajorAccountChange, RemovePersonTarget } from '_gql'

import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { useApolloQueryErrorHandling } from '@sputnik/apollo/useApolloQueryErrorHandling'
import useGetCreditAppTypes from '@sputnik/hooks/useGetCreditAppTypes'
import AddAuthorizedSignerDrawer from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/AddAuthorizedSignerDrawer'
import { useGetPeopleInfoData } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/GetPeopleInfoData.gql'
import PersonRecordWithActions from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/PersonRecordWithActions'
import PersonSection from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/PersonSection'
import { useRemovePersonFromRole } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/RemovePersonFromRole.gql'
import UpdateAuthorizedSignerDrawer from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/UpdateAuthorizedSignerDrawer'
import { addPerson } from '@sputnik/schema/creditApplication/addPerson/addPersonSchema'
import { title } from '@sputnik/schema/creditApplication/person'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

export const AUTHORIZED_SIGNER_VIEW = 'addAuthorizedSigner'

const AuthorizedSignerSection = () => {
  const { appId } = useParams()
  const showToast = useToast()
  const { hasMajorAccountChange, isMajorAccountChange } = useGetCreditAppTypes({ salesforceCreditId: appId ?? '' })
  const [isAddingAuthorizedSigner, setIsAddingAuthorizedSigner] = useState(false)
  const { tracker } = useWebTracking()
  const [removePersonFromRole] = useRemovePersonFromRole({
    onCompleted: () => {
      logInfo('removeAuthorizedSigner', {
        attributes: {
          creditAppId: appId,
          result: `authorized signer removed successfully`,
        },
        eventName: 'removeAuthorizedSigner',
      })
      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Removed authorized signer'
          id='sputnik.AuthorizedSignerSection__rUkxw1'
        />,
        {
          autoHideDelay: 5000,
        },
      )
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          creditAppId: appId,
          action: 'removeAuthorizedSigner',
          result: 'could not remove authorized signer',
        },
        eventName: 'removeAuthorizedSigner',
      })
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Failed to remove authorized signer'
          id='sputnik.AuthorizedSignerSection__uOfapS'
        />,
        { className: 'fs-unmask' },
      )
    },
  })

  const {
    data,
    loading: isLoadingAuthSignerData,
    error,
  } = useGetPeopleInfoData({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  useApolloQueryErrorHandling({
    error,
    toast: {
      message: (
        <FormattedMessage
          defaultMessage='Failed to load authorized signer data'
          id='sputnik.AuthorizedSignerSection__M/LJvo'
        />
      ),
    },
    logging: {
      eventName: 'GetAuthorizedSigner',
      message: 'could not get authorized signer',
      attributes: {
        creditAppId: appId,
      },
    },
  })

  const onDelete = async (personId: string) => {
    await removePersonFromRole({
      variables: {
        creditApplicationId: appId ?? '',
        personId,
        target: RemovePersonTarget.AUTHORIZED_SIGNER,
      },
    })
  }

  const hasAuthorizedSigner = !!data?.creditApplication?.authorizedSigner
  const { id, firstName, lastName } = data?.creditApplication?.authorizedSigner ?? {}

  const infoComplete = addPerson
    .shape({
      title: title.required(),
    })
    .isValidSync({
      ...data?.creditApplication?.authorizedSigner,
      ...data?.creditApplication?.authorizedSigner?.address,
    })

  const disableAuthorizedSignerActions =
    isMajorAccountChange && !hasMajorAccountChange(MajorAccountChange.AUTHORIZED_SIGNER)

  return (
    <PersonSection
      disableAddPersonButton={isLoadingAuthSignerData || !!id}
      subtitle={
        <FormattedMessage
          defaultMessage='*Required'
          id='sputnik.AuthorizedSignerSection__6D+soQ'
        />
      }
      title={
        <FormattedMessage
          defaultMessage='Authorized signer'
          id='sputnik.AuthorizedSignerSection__2DRSsN'
        />
      }
      onOpenDrawer={() => {
        tracker?.track('se_signup__add_person__fired', {
          person_type: 'authorizedSigner',
        })
        setIsAddingAuthorizedSigner(true)
      }}
    >
      {hasAuthorizedSigner && (
        <PersonRecordWithActions
          disableDelete={disableAuthorizedSignerActions}
          disableEdit={disableAuthorizedSignerActions}
          editDrawer={UpdateAuthorizedSignerDrawer}
          firstName={firstName || ''}
          lastName={lastName || ''}
          personId={id || ''}
          subtext={{
            showTag: false,
            infoComplete,
          }}
          trackerId='authorizedSigner'
          onDeleteClick={onDelete}
        />
      )}
      <AddAuthorizedSignerDrawer
        isShowing={isAddingAuthorizedSigner}
        onClose={() => {
          tracker?.track('se_signup__add_person_drawer_cancelled__fired', {
            person_type: 'authorizedSigner',
          })
          setIsAddingAuthorizedSigner(false)
        }}
      />
    </PersonSection>
  )
}

export default AuthorizedSignerSection
